import React from "react";
import { FIRM } from "./_shared";
import { useTheme } from "../../theme/ThemeProvider";
import CMMark from "./Mark";
import WhatsAppIcon, { whatsappUrl } from "./WhatsAppIcon";

export default function Footer() {
  const year = new Date().getFullYear();
  const { theme } = useTheme();
  const isBlakes = theme === "blakes";

  const bg = isBlakes ? "bg-sage-700 text-white" : "bg-cream-100";
  const border = isBlakes ? "border-white/10" : "border-cream-300";
  const accent = isBlakes ? "text-ochre-500" : "text-sage-600";
  const accentHover = isBlakes ? "hover:text-ochre-400" : "hover:text-sage-700";
  const logoColor = isBlakes ? "text-white" : "text-sage-600";
  const accentBar = isBlakes ? "bg-ochre-500" : "bg-sage-600";
  const muted = isBlakes ? "text-white/70" : "text-charcoal-muted";

  return (
    <footer
      data-testid="site-footer"
      className={`${bg} border-t ${border} pt-20 pb-10`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <CMMark className="h-11 w-11 mb-6" />
            <span className={`font-serif text-3xl lg:text-4xl tracking-tight leading-none block ${logoColor}`}>
              Charis <span className="italic">&amp;</span> Mettle
            </span>
            <span className={`block w-24 h-px mt-3 ${accentBar}`} />
            <p className={`mt-6 max-w-md font-serif italic text-xl leading-snug ${isBlakes ? "text-white" : "text-charcoal"}`}>
              {FIRM.tagline}
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className={`text-[11px] uppercase tracking-[0.22em] font-sans ${accent}`}>
              Services
            </p>
            <ul className={`mt-5 space-y-3 font-sans text-[14px] ${isBlakes ? "text-white" : "text-charcoal"}`}>
              {[
                "Mergers & Acquisitions",
                "Private Equity",
                "Commercial Transactions",
                "Regulatory Compliance",
                "Corporate Governance Frameworks",
              ].map((p) => (
                <li key={p}>
                  <a href="/#practice" className={`${accentHover} transition-colors`}>
                    {p}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {FIRM.offices.map((o) => (
              <div key={o.city} data-testid={`footer-office-${o.city.toLowerCase()}`}>
                <p className={`text-[11px] uppercase tracking-[0.22em] font-sans ${accent}`}>
                  {o.city} · {o.country}
                </p>
                <address className={`not-italic mt-5 space-y-1 font-sans text-[14px] ${isBlakes ? "text-white" : "text-charcoal"}`}>
                  {o.addressLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </address>
              </div>
            ))}

            <div className="sm:col-span-2 pt-4 border-t border-white/10 sm:border-t-0 sm:pt-0">
              <p className={`text-[11px] uppercase tracking-[0.22em] font-sans ${accent}`}>
                Contact
              </p>
              <div className={`mt-5 space-y-2 font-sans text-[14px] ${isBlakes ? "text-white" : "text-charcoal"}`}>
                {FIRM.offices.map((o) => (
                  <p key={o.city}>
                    <a
                      href={`tel:${o.phone.replace(/\s/g, "")}`}
                      data-testid={`footer-phone-${o.city.toLowerCase()}`}
                      className={`${accentHover} transition-colors`}
                    >
                      {o.phone}
                    </a>
                  </p>
                ))}
                <p className="pt-2">
                  <a
                    href={whatsappUrl(FIRM.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-whatsapp-link"
                    className={`inline-flex items-center gap-2 ${accentHover} transition-colors`}
                  >
                    <WhatsAppIcon className={`h-4 w-4 ${accent}`} /> Chat on WhatsApp
                  </a>
                </p>
                <p className="pt-2">
                  <a href={`mailto:${FIRM.email}`} className={`${accentHover} transition-colors`}>
                    {FIRM.email}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className={`mt-20 pt-8 border-t ${border} flex flex-col md:flex-row md:items-center justify-between gap-6`}>
          <p className={`font-sans text-[12px] ${muted}`}>
            © {year} {FIRM.name}. All rights reserved.
          </p>
          <p className={`font-sans text-[11px] uppercase tracking-[0.22em] ${muted}`}>
            This website is for informational purposes and does not constitute legal advice.
          </p>
        </div>
      </div>
    </footer>
  );
}
